import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

export type AuthFlowCopy = {
  checkYourEmail: string;
  verificationCodeSent: string;
  verificationCode: string;
  verifying: string;
  verifyAndSignIn: string;
  resendCode: string;
  backToSignIn: string;
  enterVerificationCode: string;
  loggingYouIn: string;
  authSessionWait: string;
};

const COPY_BY_LOCALE: Record<SupportedLocale, AuthFlowCopy> = {
  en: {
    checkYourEmail: 'Check your email',
    verificationCodeSent: 'We sent a verification code to',
    verificationCode: 'Verification code',
    verifying: 'Verifying...',
    verifyAndSignIn: 'Verify and Sign In',
    resendCode: 'Resend code',
    backToSignIn: 'Back to sign in',
    enterVerificationCode: 'Enter verification code',
    loggingYouIn: 'Logging you in',
    authSessionWait: 'Hang tight while we securely authenticate your session.',
  },
  'pt-BR': {
    checkYourEmail: 'Verifique seu e-mail.',
    verificationCodeSent:
      'Enviamos um c\u00F3digo de verifica\u00E7\u00E3o para',
    verificationCode: 'C\u00F3digo de verifica\u00E7\u00E3o.',
    verifying: 'Verificando...',
    verifyAndSignIn: 'Verifique e entre.',
    resendCode: 'Reenviar c\u00F3digo',
    backToSignIn: 'Voltar para assinar.',
    enterVerificationCode: 'Digite o c\u00F3digo de verifica\u00E7\u00E3o.',
    loggingYouIn: 'Estou entrando.',
    authSessionWait: 'Aguente firme enquanto autenticamos sua sess\u00E3o.',
  },
  ar: {
    checkYourEmail: 'تحقق من بريدك الإلكتروني',
    verificationCodeSent: 'أرسلنا رمز تحقق إلى',
    verificationCode: 'رمز التحقق',
    verifying: 'جارٍ التحقق...',
    verifyAndSignIn: 'تحقق وسجّل الدخول',
    resendCode: 'إعادة إرسال الرمز',
    backToSignIn: 'العودة إلى تسجيل الدخول',
    enterVerificationCode: 'أدخل رمز التحقق',
    loggingYouIn: 'جارٍ تسجيل دخولك',
    authSessionWait: 'انتظر لحظة بينما نتحقق من جلستك بأمان.',
  },
  es: {
    checkYourEmail: 'Revisa tu correo electrónico',
    verificationCodeSent: 'Hemos enviado un código de verificación a',
    verificationCode: 'Código de verificación',
    verifying: 'Verificando...',
    verifyAndSignIn: 'Verificar e iniciar sesión',
    resendCode: 'Reenviar código',
    backToSignIn: 'Volver a iniciar sesión',
    enterVerificationCode: 'Introduce el código de verificación',
    loggingYouIn: 'Iniciando sesión',
    authSessionWait: 'Espera mientras autenticamos tu sesión de forma segura.',
  },
  fr: {
    checkYourEmail: 'Consultez votre e-mail',
    verificationCodeSent: 'Nous avons envoyé un code de vérification à',
    verificationCode: 'Code de vérification',
    verifying: 'Vérification...',
    verifyAndSignIn: 'Vérifier et se connecter',
    resendCode: 'Renvoyer le code',
    backToSignIn: 'Retour à la connexion',
    enterVerificationCode: 'Saisissez le code de vérification',
    loggingYouIn: 'Connexion en cours',
    authSessionWait: 'Patientez pendant que nous authentifions votre session.',
  },
  it: {
    checkYourEmail: 'Controlla la tua email',
    verificationCodeSent: 'Abbiamo inviato un codice di verifica a',
    verificationCode: 'Codice di verifica',
    verifying: 'Verifica in corso...',
    verifyAndSignIn: 'Verifica e accedi',
    resendCode: 'Invia di nuovo il codice',
    backToSignIn: 'Torna all’accesso',
    enterVerificationCode: 'Inserisci il codice di verifica',
    loggingYouIn: 'Accesso in corso',
    authSessionWait:
      'Attendi mentre autentichiamo la tua sessione in modo sicuro.',
  },
  ko: {
    checkYourEmail: '이메일을 확인하세요',
    verificationCodeSent: '다음 주소로 인증 코드를 보냈습니다:',
    verificationCode: '인증 코드',
    verifying: '인증 중...',
    verifyAndSignIn: '인증하고 로그인',
    resendCode: '코드 다시 보내기',
    backToSignIn: '로그인으로 돌아가기',
    enterVerificationCode: '인증 코드를 입력하세요',
    loggingYouIn: '로그인 중',
    authSessionWait: '세션을 안전하게 인증하는 동안 잠시 기다려 주세요.',
  },
  de: {
    checkYourEmail: 'Prüfe deine E-Mails',
    verificationCodeSent: 'Wir haben einen Bestätigungscode gesendet an',
    verificationCode: 'Bestätigungscode',
    verifying: 'Wird bestätigt...',
    verifyAndSignIn: 'Bestätigen und anmelden',
    resendCode: 'Code erneut senden',
    backToSignIn: 'Zurück zur Anmeldung',
    enterVerificationCode: 'Bestätigungscode eingeben',
    loggingYouIn: 'Du wirst angemeldet',
    authSessionWait:
      'Bitte warte, während wir deine Sitzung sicher authentifizieren.',
  },
  ja: {
    checkYourEmail: 'メールをご確認ください',
    verificationCodeSent: '確認コードを次のメールアドレスに送信しました：',
    verificationCode: '確認コード',
    verifying: '確認中...',
    verifyAndSignIn: '確認してログイン',
    resendCode: 'コードを再送信',
    backToSignIn: 'ログインに戻る',
    enterVerificationCode: '確認コードを入力してください',
    loggingYouIn: 'ログインしています',
    authSessionWait:
      'セッションを安全に認証しています。しばらくお待ちください。',
  },
  hi: {
    checkYourEmail: 'अपना ईमेल देखें',
    verificationCodeSent: 'हमने सत्यापन कोड भेजा है:',
    verificationCode: 'सत्यापन कोड',
    verifying: 'सत्यापन हो रहा है...',
    verifyAndSignIn: 'सत्यापित करें और साइन इन करें',
    resendCode: 'कोड दोबारा भेजें',
    backToSignIn: 'साइन इन पर वापस जाएँ',
    enterVerificationCode: 'सत्यापन कोड दर्ज करें',
    loggingYouIn: 'आपको लॉग इन किया जा रहा है',
    authSessionWait:
      'कृपया प्रतीक्षा करें, हम आपके सत्र को सुरक्षित रूप से प्रमाणित कर रहे हैं।',
  },
  ru: {
    checkYourEmail: 'Проверьте электронную почту',
    verificationCodeSent: 'Мы отправили код подтверждения на',
    verificationCode: 'Код подтверждения',
    verifying: 'Проверяем...',
    verifyAndSignIn: 'Подтвердить и войти',
    resendCode: 'Отправить код повторно',
    backToSignIn: 'Вернуться ко входу',
    enterVerificationCode: 'Введите код подтверждения',
    loggingYouIn: 'Выполняется вход',
    authSessionWait: 'Подождите, пока мы безопасно проверим вашу сессию.',
  },
  zh: {
    checkYourEmail: '请查看你的邮箱',
    verificationCodeSent: '我们已将验证码发送至',
    verificationCode: '验证码',
    verifying: '正在验证...',
    verifyAndSignIn: '验证并登录',
    resendCode: '重新发送验证码',
    backToSignIn: '返回登录',
    enterVerificationCode: '请输入验证码',
    loggingYouIn: '正在登录',
    authSessionWait: '请稍候，我们正在安全验证你的会话。',
  },
  'zh-TW': {
    checkYourEmail: '請查看你的電子郵件',
    verificationCodeSent: '我們已將驗證碼傳送至',
    verificationCode: '驗證碼',
    verifying: '正在驗證...',
    verifyAndSignIn: '驗證並登入',
    resendCode: '重新傳送驗證碼',
    backToSignIn: '返回登入',
    enterVerificationCode: '請輸入驗證碼',
    loggingYouIn: '正在登入',
    authSessionWait: '請稍候，我們正在安全驗證你的工作階段。',
  },
  vi: {
    checkYourEmail: 'Kiểm tra email của bạn',
    verificationCodeSent: 'Chúng tôi đã gửi mã xác minh đến',
    verificationCode: 'Mã xác minh',
    verifying: 'Đang xác minh...',
    verifyAndSignIn: 'Xác minh và đăng nhập',
    resendCode: 'Gửi lại mã',
    backToSignIn: 'Quay lại đăng nhập',
    enterVerificationCode: 'Nhập mã xác minh',
    loggingYouIn: 'Đang đăng nhập',
    authSessionWait: 'Vui lòng chờ trong khi chúng tôi xác thực phiên của bạn.',
  },
  id: {
    checkYourEmail: 'Periksa email Anda',
    verificationCodeSent: 'Kami telah mengirim kode verifikasi ke',
    verificationCode: 'Kode verifikasi',
    verifying: 'Memverifikasi...',
    verifyAndSignIn: 'Verifikasi dan masuk',
    resendCode: 'Kirim ulang kode',
    backToSignIn: 'Kembali ke halaman masuk',
    enterVerificationCode: 'Masukkan kode verifikasi',
    loggingYouIn: 'Sedang memasukkan Anda',
    authSessionWait:
      'Harap tunggu sementara kami mengautentikasi sesi Anda dengan aman.',
  },
};

export function getAuthFlowCopy(locale: string): AuthFlowCopy {
  return COPY_BY_LOCALE[normalizeLocale(locale)];
}
